/**
 * @description User-facing auth error messages for admin portal sign-in
 *
 * Re-exports merchant auth error resolver with admin-specific overrides.
 *
 * @dependencies
 * - None (mirrors frontend/src/utils/authErrorMessages.js)
 *
 * @notes
 * - Kept in sync with merchant frontend authErrorMessages.js
 */

/** OAuth redirect error codes surfaced on /sign-in */
const OAUTH_URL_ERROR_MESSAGES = {
  access_denied: 'Access was denied. Please try signing in again.',
  invalid_request: 'Your sign-in session is invalid. Please try again.',
  invalid_state: 'Your sign-in session expired. Please try again.',
  invalid_callback: 'Sign-in could not be completed. Please try again.',
  invalid_token: 'Your sign-in link expired. Please try again.',
  session_expired: 'Your session has expired. Please sign in again.',
  callback_failed: 'Sign-in failed. Please try again.',
  login_failed: 'Sign-in could not be started. Please try again.',
  login_required: 'Please sign in to continue.',
  consent_required: 'Additional permissions are required. Please try again.',
};

const SIGN_IN_ERROR_MESSAGES = {
  AUTH_INVALID_CREDENTIALS: 'Invalid email or password.',
  AUTH_MISSING_CREDENTIALS: 'Please enter your email and password.',
  AUTH_MISSING_EMAIL: 'Please enter your email address.',
  AUTH_MISSING_PASSWORD: 'Please enter your password.',
  AUTH_INVALID_EMAIL: 'Please enter a valid email address.',
  AUTH_USER_LOCKED: 'Account locked. Contact a platform administrator.',
  AUTH_USER_DISABLED: 'This account has been disabled.',
  AUTH_EMAIL_NOT_VERIFIED: 'Please verify your email address before signing in.',
  AUTH_PASSWORD_EXPIRED: 'Your password has expired. Please reset your password.',
  AUTH_PASSWORD_RESET_REQUIRED: 'You must reset your password before signing in.',
  AUTH_MFA_ENROLLMENT_REQUIRED: 'Multi-factor authentication setup is required.',
  AUTH_MFA_CHALLENGE_REQUIRED: 'Multi-factor authentication verification is required.',
  AUTH_MISSING_REQUEST_TOKEN: 'Your sign-in session expired. Restarting sign-in…',
  MISSING_AUTH_REQUEST: 'Your sign-in session expired. Restarting sign-in…',
  AUTH_SESSION_EXPIRED: 'Your session has expired. Please sign in again.',
  AUTH_RATE_LIMITED: 'Too many login attempts. Please wait and try again.',
  AUTH_SERVICE_UNAVAILABLE: 'Sign-in is temporarily unavailable. Please try again shortly.',
  AUTH_TIMEOUT: 'Sign-in timed out. Please check your connection and try again.',
  AUTH_LOGIN_FAILED: 'Unable to sign in right now. Please try again.',
  AUTH_BAD_REQUEST: 'Please check your email and password and try again.',
  AUTH_VALIDATION_FAILED: 'Please check your email and password and try again.',
};

/**
 * Resolve OAuth URL error param to a user-facing message
 *
 * @param {string|null|undefined} urlError
 * @returns {string|null}
 */
export function resolveOAuthUrlError(urlError) {
  if (!urlError) {
    return null;
  }
  return OAUTH_URL_ERROR_MESSAGES[urlError] || 'Sign-in failed. Please try again.';
}

/**
 * Resolve sign-in API error to a user-facing message
 *
 * @param {object} params
 * @param {string} [params.errorCode]
 * @param {string} [params.errorMessage]
 * @param {number} [params.httpStatus]
 * @returns {string}
 */
export function resolveSignInError({ errorCode, errorMessage, httpStatus }) {
  if (errorCode && SIGN_IN_ERROR_MESSAGES[errorCode]) {
    return SIGN_IN_ERROR_MESSAGES[errorCode];
  }

  if (httpStatus === 429) {
    return SIGN_IN_ERROR_MESSAGES.AUTH_RATE_LIMITED;
  }

  if (errorMessage) {
    return errorMessage;
  }

  return 'Unable to sign in. Please check your connection and try again.';
}

/**
 * @param {string} errorCode
 * @returns {boolean}
 */
export function shouldRestartSignInFlow(errorCode) {
  return errorCode === 'AUTH_MISSING_REQUEST_TOKEN' || errorCode === 'MISSING_AUTH_REQUEST';
}
